"use client";

/* eslint-disable react/no-unescaped-entities */
/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button, IconButton, InputAdornment, TextField, Box, MenuItem, Select, SelectChangeEvent, CircularProgress } from '@mui/material';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useLogin } from '@/services/auth/mutations';
import { useAppDispatch } from '@/lib/hook';
import { setCredentials, setUser } from '@/lib/authSlice';
import toast from 'react-hot-toast';

import { Roles } from '@/types/auth';
import { getFromStorage } from '@/helpers/storage';
import { STORAGE_KEYS } from '@/constants/storageKeys';

function AuthMain() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ usernameOrEmail: '', password: '' });

  const { mutate: login, isPending } = useLogin();

  // Mapping using the Roles enum
  const roleConfig: Record<string, string> = {
    [Roles.CSO]: "/cso/dashboard",
    [Roles.EVALUATOR]: "/evaluator/dashboard",
    [Roles.SUPERVISOR]: "/supervisor/dashboard",
    [Roles.MALL_MANAGER]: "/mall-manager/dashboard",
    [Roles.QA]: "/qa/dashboard",
    [Roles.CS]: "/cs-manager/dashboard",
    [Roles.SUPER_ADMIN]: "/dashboard", // Fallback/Default for Super Admin for now
  };

  const dispatch = useAppDispatch()

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();

    // Reset errors
    setErrors({ usernameOrEmail: '', password: '' });

    // Validation logic
    let hasError = false;
    const newErrors = { usernameOrEmail: '', password: '' };

    if (!usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or Email is required';
      hasError = true;
    }
    if (!password.trim()) {
      newErrors.password = 'Password is required';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields');
      return;
    }

    login(
      {
        username_or_email: usernameOrEmail, password,
        deviceToken: getFromStorage(STORAGE_KEYS.fcmToken), panel_type: 3,
        device_type: 3
      },
      {
        onSuccess: (data) => {
          // Assuming the first group represents the role
          dispatch(setCredentials({ user: data?.me, token: data?.access }));
          dispatch(setUser(data?.me))
          // API returns role exactly as in enum
          const userRole = data?.me?.groups?.[0];
          const targetPath = userRole ? roleConfig[userRole] : undefined;
          if (targetPath) {
            router.push(targetPath);
          } else {
            console.error('Unknown user role:', userRole);
            toast.error("Access Denied: You do not have a valid role assigned.");
          }
        },
        onError: (error: any) => {
          console.error('Login failed:', error);
          const apiMessage = error?.response?.data?.message || error?.message || 'Something went wrong';
          toast.error(apiMessage);
        },
      }
    );
  };


  return (
    <main className="content auth_wrap">
      <section className='auth_sc'>
        <div className="inner">
          <div className="lt">
            <Box component="a" className="site_logo">
              <figure>
                <img src="/images/maf_logo.svg" alt="Logo" />
              </figure>
            </Box>
            <h1 className='fw_med'>Performance Management</h1>
            <div className="secure">
              <figure><img src="/images/lock_icon.svg" alt="Icon" /></figure>
              <p>
                <strong>Secure SSO Authentication</strong>
                <span>Login using your MAF Single Sign-On credentials</span>
              </p>
            </div>
          </div>
          <div className="rt">
            <div className="auth_form">
              <div className="s_head hd_4">
                <h2>Welcome Back</h2>
                <p>Enter your MAF credentials to access your dashboard</p>
              </div>
              <form className="form" onSubmit={handleLogin}>
                <div className="gap_p">

                  <div className="control_group w_100">
                    <label htmlFor="field_usernameOrEmail">Username or Email</label>
                    <TextField
                      id="field_usernameOrEmail"
                      fullWidth
                      hiddenLabel
                      placeholder="Enter Username or Email"
                      value={usernameOrEmail}
                      onChange={(e) => {
                        setUsernameOrEmail(e.target.value);
                        if (errors.usernameOrEmail) setErrors(prev => ({ ...prev, usernameOrEmail: '' }));
                      }}
                      disabled={isPending}
                      error={!!errors.usernameOrEmail}
                      helperText={errors.usernameOrEmail}
                    />
                  </div>

                  <div className="control_group w_100">
                    <label htmlFor="field_password">Password</label>
                    <TextField
                      id="field_password"
                      fullWidth
                      hiddenLabel
                      placeholder="Enter password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({ ...prev, password: '' }));
                      }}
                      disabled={isPending}
                      error={!!errors.password}
                      helperText={errors.password}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end" className="eye_icon">
                              <IconButton
                                aria-label={
                                  showPassword
                                    ? "hide the password"
                                    : "display the password"
                                }
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <VisibilityOffOutlinedIcon />
                                ) : (
                                  <VisibilityOutlinedIcon />
                                )}
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="form_btn">
                  <Button
                    type="submit"
                    className="w_100"
                    disabled={isPending}
                    startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {isPending ? 'Signing in...' : 'Sign in with MAF'}
                  </Button>
                </div>

                <div className="note">
                  <p>By signing in, you agree to MAF's Terms of Service and Privacy Policy.</p>
                  <p>For assistance, contact IT Support at support@maf.ae</p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section >
    </main>
  )
}

export default AuthMain

